import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sprout, Dumbbell, Smile, Leaf, Cog, FlaskConical, ClipboardCheck, Package,
  MessageCircle, Instagram, Facebook, ArrowRight, Briefcase, ShoppingBag,
  Truck, Coffee, ChevronDown,
} from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import ProductCard from "@/shop/ProductCard";
import { useProducts } from "@/shop/ProductsContext";

const WHATSAPP = process.env.REACT_APP_WHATSAPP_NUMBER || "919003140803";
const WA_QR_MESSAGE = "Hi Zentra! I scanned the QR code on my Zentra protein bar and would like to know more/order Zentra.";
const waLink = (msg = WA_QR_MESSAGE) => `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(msg)}`;

/* ---- EDITABLE CONFIG — update these as real data is confirmed ---- */
const OFFER = { active: false, badge: "SCAN & SAVE", text: "Get ₹50 OFF your first eligible Zentra box." };
const NUTRITION = [
  { label: "Serving size", value: "1 bar (50 g)" },
  { label: "Energy", value: null },
  { label: "Protein", value: null },
  { label: "Carbohydrate", value: null },
  { label: "Total sugars", value: null },
  { label: "Added sugars", value: "0 g" },
  { label: "Fat", value: null },
  { label: "Saturated fat", value: null },
  { label: "Fibre", value: null },
  { label: "Sodium", value: null },
  { label: "Horse Gram microgreens", value: "2 g" },
];
const INGREDIENTS = [
  "Dates", "Soy Protein Isolate", "Date Syrup", "Cocoa Solids", "Millet Crispy Ball",
  "Raw Honey", "Ghee", "Almond", "Cashew", "Horse Gram Microgreen Powder",
  "Nature-Identical Chocolate Flavour", "Soy Lecithin",
];
/* ------------------------------------------------------------------ */

const scrollToId = (id) => {
  const el = document.querySelector(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -70, duration: 1.2 });
  else el.scrollIntoView({ behavior: "smooth" });
};

const EASE = [0.16, 1, 0.3, 1];
const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.7, delay, ease: EASE }}
    className={className}
  >
    {children}
  </motion.div>
);

const SectionHead = ({ overline, title, text, compact = false }) => (
  <Reveal className={compact ? "mb-6" : "mb-10"}>
    {overline && (
      <p className={`text-[11px] font-semibold uppercase tracking-[0.25em] text-[#7ED321] ${compact ? "mb-2" : "mb-3"}`}>{overline}</p>
    )}
    <h2 className="font-display text-3xl uppercase leading-[0.95] text-white sm:text-4xl">{title}</h2>
    {text && <p className={`text-sm leading-relaxed text-[#A6A66A] sm:text-base ${compact ? "mt-2" : "mt-4"} max-w-xl`}>{text}</p>}
  </Reveal>
);

const btnPrimary =
  "inline-flex items-center justify-center gap-2 rounded-full bg-[#7ED321] px-8 py-4 text-sm font-bold uppercase tracking-[0.12em] text-[#0A0A0A] transition-colors duration-300 hover:bg-[#8FE331]";
const btnWhats =
  "inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-8 py-4 text-sm font-bold uppercase tracking-[0.12em] text-white transition-colors duration-300 hover:bg-[#2be374]";
const btnGhost =
  "inline-flex items-center justify-center gap-2 rounded-full border border-white/20 px-8 py-4 text-sm font-bold uppercase tracking-[0.12em] text-white transition-colors duration-300 hover:border-[#7ED321] hover:text-[#7ED321]";

const MeetZentra = () => {
  const [nutritionOpen, setNutritionOpen] = useState(false);
  const location = useLocation();
  const { products } = useProducts();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash;
      const t = setTimeout(() => {
        const el = document.querySelector(id);
        if (!el) return;
        if (window.__lenis) window.__lenis.scrollTo(el, { offset: -70, duration: 1.2 });
        else el.scrollIntoView({ behavior: "smooth" });
      }, 450);
      return () => clearTimeout(t);
    }
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [location.pathname, location.hash]);

  return (
    <div className="bg-[#0A0A0A] text-white" data-testid="meet-zentra-page">
      <Helmet>
        <title>Zentra | Everyday Protein. Powered by Microgreens.</title>
        <meta
          name="description"
          content="Everyday Protein. Powered by Microgreens. Zentra protein bars made with horse gram microgreens we grow ourselves. Order online from Coimbatore."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://zentrafoods.in/meet-zentra" />
        <meta property="og:title" content="Zentra | Everyday Protein. Powered by Microgreens." />
        <meta
          property="og:description"
          content="Everyday Protein. Powered by Microgreens. Zentra protein bars made with horse gram microgreens we grow ourselves. Order online from Coimbatore."
        />
        <meta property="og:image" content="https://zentrafoods.in/assets/zentra-logo.png" />
      </Helmet>

      {/* Shared site header */}
      <SiteHeader />

      <main className="pb-24 md:pb-0">
        {/* HERO */}
        <section id="meet-hero" data-testid="meet-hero" className="relative overflow-hidden px-5 pb-14 pt-28 lg:px-12">
          <div className="spotlight absolute inset-0" aria-hidden="true" />
          <div className="relative mx-auto max-w-[1400px]">
            <Reveal>
              <p className="mb-4 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-[#7ED321]">
                <span className="pulse-dot h-2 w-2 rounded-full bg-[#7ED321]" />
                Everyday Protein. Powered by Microgreens.
              </p>
              <h1 className="font-display text-6xl uppercase leading-[0.9] text-white sm:text-7xl" data-testid="meet-headline">
                Meet <span className="text-[#7ED321]">Zentra</span>
              </h1>
              <p className="mt-3 font-display text-xl uppercase tracking-wide text-white/80 sm:text-2xl">
                The protein bar made with horse gram microgreens we grow ourselves.
              </p>
              <p className="mt-5 max-w-xl text-sm leading-relaxed text-[#A6A66A] sm:text-base">
                You've just opened a different kind of protein bar. Zentra combines quality
                protein with microgreens to create a nutritious, convenient snack for everyday life.
              </p>
            </Reveal>

            <Reveal delay={0.1} className="mt-8">
              <img
                src="/assets/hero-bar-box.webp"
                alt="Zentra 50 g Horse Gram Microgreens Protein Bar — retail box"
                className="mx-auto w-full max-w-2xl rounded-2xl border border-white/10 shadow-[0_24px_60px_rgba(0,0,0,0.5)]"
                data-testid="meet-hero-bar"
              />
            </Reveal>

            <Reveal delay={0.15} className="mt-6 flex flex-wrap justify-center gap-6">
              {[
                { icon: Sprout, label: "Microgreens" },
                { icon: Dumbbell, label: "Protein" },
                { icon: Smile, label: "Great-tasting snack" },
              ].map((b) => (
                <div key={b.label} className="flex items-center gap-2" data-testid={`meet-benefit-${b.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <b.icon className="h-4 w-4 text-[#7ED321]" />
                  <span className="text-xs font-medium uppercase tracking-[0.12em] text-white/80">{b.label}</span>
                </div>
              ))}
            </Reveal>

            <Reveal delay={0.2} className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
              <a href="/shop" data-testid="meet-hero-shop-button" className={btnWhats}>
                <ShoppingBag className="h-4 w-4" />
                Shop Now
              </a>
            </Reveal>
          </div>
        </section>

        {/* WHAT MAKES ZENTRA DIFFERENT */}
        <section id="different" data-testid="meet-different" className="border-t border-white/10 px-5 py-16 lg:px-12">
          <div className="mx-auto max-w-[1400px]">
            <SectionHead
              overline="The Difference"
              title="What makes Zentra different?"
              text="Most protein bars focus on protein alone. At Zentra, we wanted to explore something more — combining protein with the goodness of microgreens in a convenient everyday snack. Our journey began with growing microgreens and understanding how they could become part of everyday nutrition. Zentra brings that journey from the farm to your snack."
            />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              {[
                { icon: Sprout, t: "Microgreens", d: "Carefully grown and processed microgreens form an important part of the Zentra story." },
                { icon: Dumbbell, t: "Protein", d: "A convenient way to add protein to your everyday diet." },
                { icon: Smile, t: "Great-tasting snack", d: "Nutrition should be enjoyable, convenient and easy to make part of your day." },
              ].map((c, i) => (
                <Reveal key={c.t} delay={i * 0.08}>
                  <div data-testid={`meet-card-${c.t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="h-full rounded-2xl border border-white/10 bg-[#121212] p-6 transition-colors duration-300 hover:border-[#7ED321]/40">
                    <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#7ED321]/10">
                      <c.icon className="h-5 w-5 text-[#7ED321]" />
                    </div>
                    <h3 className="mt-5 font-display text-xl uppercase tracking-wide text-white">{c.t}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-[#A6A66A]">{c.d}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* WHAT ARE MICROGREENS */}
        <section id="microgreens" data-testid="meet-microgreens" className="border-t border-white/10 px-5 py-16 lg:px-12">
          <div className="mx-auto grid max-w-[1400px] grid-cols-1 items-center gap-10 lg:grid-cols-2">
            <div>
              <SectionHead
                overline="The Green Inside"
                title="What are microgreens?"
                text="Microgreens are young edible plants harvested at an early stage of growth. They are valued for their distinctive flavours, colours and nutritional composition. At Zentra, we are exploring how microgreens can be incorporated into convenient everyday foods."
              />
              <Reveal>
                <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-[#121212] p-5" data-testid="meet-seed-process">
                  {["Seed", "Microgreen", "Harvest"].map((s, i) => (
                    <div key={s} className="flex flex-1 items-center gap-3">
                      <div className="flex flex-1 flex-col items-center gap-2 text-center">
                        <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#7ED321]/10">
                          {i === 0 && <Leaf className="h-5 w-5 text-[#7ED321]" />}
                          {i === 1 && <Sprout className="h-5 w-5 text-[#7ED321]" />}
                          {i === 2 && <Package className="h-5 w-5 text-[#7ED321]" />}
                        </div>
                        <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-white/70">{s}</span>
                      </div>
                      {i < 2 && <ArrowRight className="h-4 w-4 shrink-0 text-[#7ED321]/60" />}
                    </div>
                  ))}
                </div>
                <a href="/microgreens" data-testid="meet-explore-journey"
                  className="mt-6 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#7ED321] transition-colors duration-300 hover:text-[#8FE331]">
                  Explore Our Microgreens Journey
                  <ArrowRight className="h-4 w-4" />
                </a>
              </Reveal>
            </div>
            <Reveal delay={0.1}>
              <div className="grid grid-cols-2 gap-3">
                <img src="/assets/farm-growroom-wide.jpg" alt="Microgreens growing at the Zentra indoor farm" loading="lazy"
                  className="aspect-[3/4] w-full rounded-2xl border border-white/10 object-cover" />
                <img src="/assets/farm-trays.jpg" alt="Microgreen trays at the Zentra farm" loading="lazy"
                  className="mt-8 aspect-[3/4] w-full rounded-2xl border border-white/10 object-cover" />
              </div>
            </Reveal>
          </div>
        </section>

        {/* FROM MICROGREENS TO ZENTRA — timeline */}
        <section id="process" data-testid="meet-process" className="border-t border-white/10 px-5 py-16 lg:px-12">
          <div className="mx-auto max-w-[1400px]">
            <SectionHead overline="The Journey" title="From Microgreens to Zentra" />
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-6 lg:gap-3">
              {[
                { n: "01", t: "Grow", icon: Sprout, d: "We grow microgreens with care, focusing on consistent quality." },
                { n: "02", t: "Harvest", icon: Leaf, d: "Microgreens are harvested at the appropriate stage." },
                { n: "03", t: "Process", icon: Cog, d: "They are carefully processed into a convenient ingredient." },
                { n: "04", t: "Formulate", icon: FlaskConical, d: "The microgreens ingredient is incorporated into our protein-bar formulation." },
                { n: "05", t: "Test", icon: ClipboardCheck, d: "Product and ingredient testing helps us understand quality, nutrition and consistency." },
                { n: "06", t: "Enjoy", icon: Smile, d: "The result is Zentra — a convenient protein snack with a microgreens story." },
              ].map((s, i) => (
                <Reveal key={s.n} delay={i * 0.06}>
                  <div data-testid={`meet-step-${s.n}`}
                    className="relative h-full rounded-2xl border border-white/10 bg-[#121212] p-5 transition-colors duration-300 hover:border-[#7ED321]/40 lg:p-4">
                    <div className="flex items-center justify-between">
                      <s.icon className="h-5 w-5 text-[#7ED321]" />
                      <span className="font-display text-2xl text-white/10">{s.n}</span>
                    </div>
                    <h3 className="mt-4 font-display text-lg uppercase tracking-wide text-white">{s.t}</h3>
                    <p className="mt-2 text-xs leading-relaxed text-[#A6A66A]">{s.d}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* WHAT'S INSIDE */}
        <section id="whats-inside" data-testid="meet-nutrition" className="border-t border-white/10 px-5 py-16 lg:px-12">
          <div className="mx-auto max-w-[900px]">
            <SectionHead
              overline="Inside the Bar"
              title="What's inside your Zentra?"
              text="Every Zentra bar is a 50 g everyday snack with protein and 2 g of Horse Gram microgreens. Final lab-verified nutrition values will be published here once the product formulation is locked."
            />
            <Reveal>
              <div className="rounded-2xl border border-white/10 bg-[#121212] p-6">
                <div className="flex items-center justify-between">
                  <span className="font-display text-2xl uppercase tracking-wide text-white">50 g bar</span>
                  <span className="rounded-full bg-[#7ED321]/10 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-[#7ED321]">
                    2 g Horse Gram Microgreens
                  </span>
                </div>
                <p className="mt-6 text-[11px] font-semibold uppercase tracking-[0.25em] text-white/40">
                  Ingredients
                </p>
                <div className="mt-3 flex flex-wrap gap-2" data-testid="meet-ingredients-list">
                  {INGREDIENTS.map((ing) => (
                    <span
                      key={ing}
                      className={`rounded-full border px-3.5 py-1.5 text-[11px] font-medium ${
                        ing.includes("Microgreen")
                          ? "border-[#7ED321]/50 bg-[#7ED321]/10 text-[#7ED321]"
                          : "border-white/15 text-white/70"
                      }`}
                    >
                      {ing}
                    </span>
                  ))}
                </div>
                <p className="mt-4 text-[11px] leading-relaxed text-white/40">
                  Contains soy and nuts (almond, cashew). Made with ghee and raw honey.
                </p>
                <button
                  onClick={() => setNutritionOpen(!nutritionOpen)}
                  data-testid="meet-nutrition-toggle"
                  className={`${btnGhost} mt-6 w-full sm:w-auto`}
                >
                  View Nutrition Information
                  <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${nutritionOpen ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence>
                  {nutritionOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.4, ease: EASE }}
                      className="overflow-hidden"
                      data-testid="meet-nutrition-table"
                    >
                      <div className="mt-6 divide-y divide-white/10 border-t border-white/10">
                        {NUTRITION.map((row) => (
                          <div key={row.label} className="flex items-center justify-between py-3 text-sm">
                            <span className="text-white/80">{row.label}</span>
                            <span className={row.value ? "font-semibold text-white" : "text-white/35"}>
                              {row.value ?? "To be confirmed"}
                            </span>
                          </div>
                        ))}
                      </div>
                      <p className="mt-4 text-[11px] leading-relaxed text-white/40">
                        Placeholder values marked "To be confirmed" will be updated with final
                        lab-verified nutrition information from the approved product formulation.
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </Reveal>
          </div>
        </section>

        {/* QUALITY & TESTING */}
        <section id="testing" data-testid="meet-testing" className="border-t border-white/10 px-5 py-16 lg:px-12">
          <div className="mx-auto max-w-[1400px]">
            <SectionHead
              overline="Quality"
              title="Developed with testing in mind"
              text="We believe good food innovation should combine thoughtful formulation with testing and evidence. Zentra products are being evaluated through appropriate laboratory testing to understand nutritional composition, quality and product characteristics."
            />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              {[
                { icon: ClipboardCheck, t: "Nutrition", d: "Understanding the nutritional composition of the product." },
                { icon: FlaskConical, t: "Quality", d: "Evaluating relevant product quality parameters." },
                { icon: Cog, t: "Product Development", d: "Using testing and feedback to continuously improve the product." },
              ].map((c, i) => (
                <Reveal key={c.t} delay={i * 0.08}>
                  <div data-testid={`meet-testing-${c.t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="h-full rounded-2xl border border-white/10 bg-[#121212] p-6 transition-colors duration-300 hover:border-[#7ED321]/40">
                    <c.icon className="h-5 w-5 text-[#7ED321]" />
                    <h3 className="mt-4 font-display text-xl uppercase tracking-wide text-white">{c.t}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-[#A6A66A]">{c.d}</p>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.15} className="mt-8 flex flex-col gap-3 sm:flex-row">
              <a href="/assets/zentra-microgreens-lab-report.pdf" target="_blank" rel="noopener noreferrer"
                data-testid="meet-view-testing-info" className={btnGhost}>
                View Testing Information
              </a>
            </Reveal>
          </div>
        </section>

        {/* WHY ZENTRA */}
        <section id="why" data-testid="meet-why" className="border-t border-white/10 px-5 py-16 lg:px-12">
          <div className="mx-auto grid max-w-[1400px] grid-cols-1 items-center gap-10 lg:grid-cols-2">
            <Reveal>
              <img src="/assets/farm-growroom-team.jpg" alt="The Zentra team among the microgreen racks" loading="lazy"
                className="aspect-[4/3] w-full rounded-2xl border border-white/10 object-cover" />
            </Reveal>
            <div>
              <SectionHead
                overline="Our Reason"
                title="Why did we create Zentra?"
                text="Zentra started with a simple question: Can nutritious food become something people genuinely enjoy eating every day? Our journey began with microgreens and a desire to create better opportunities for growers while making nutrient-rich food more accessible and convenient. We are building Zentra at the intersection of nutrition, agriculture and innovation."
              />
              <Reveal>
                <p className="rounded-2xl border-l-2 border-[#7ED321] bg-[#121212] p-6 font-display text-xl uppercase leading-snug tracking-wide text-white sm:text-2xl"
                  data-testid="meet-why-highlight">
                  "From growers to consumers, we want to create value at every step."
                </p>
              </Reveal>
            </div>
          </div>
        </section>

        {/* FOUNDERS */}
        <section id="founders" data-testid="meet-founders" className="border-t border-white/10 px-5 py-16 lg:px-12">
          <div className="mx-auto max-w-[1400px]">
            <SectionHead overline="The Team" title="The people behind Zentra" />
            <Reveal>
              <img src="/assets/founders-team.webp" alt="Ravikumar Kandadai and Rohit Sridharan at the Mountain Fresh Farms facility"
                loading="lazy" className="w-full rounded-2xl border border-white/10 object-cover" />
            </Reveal>
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Reveal delay={0.05}>
                <div className="h-full rounded-2xl border border-white/10 bg-[#121212] p-6" data-testid="meet-founder-ravikumar">
                  <h3 className="font-display text-2xl uppercase tracking-wide text-white">Ravikumar Kandadai</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#A6A66A]">
                    With a background spanning botany, agriculture and finance, Ravikumar brings
                    decades of experience and a deep interest in agriculture, food and
                    sustainable opportunities.
                  </p>
                </div>
              </Reveal>
              <Reveal delay={0.1}>
                <div className="h-full rounded-2xl border border-white/10 bg-[#121212] p-6" data-testid="meet-founder-Rohit Sridharan">
                  <h3 className="font-display text-2xl uppercase tracking-wide text-white">Rohit Sridharan</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#A6A66A]">
                    With a background in technology and business, Rohit Sridharan brings a focus on
                    innovation, digital growth and building a scalable bridge between
                    agriculture and consumers.
                  </p>
                </div>
              </Reveal>
            </div>
            <Reveal delay={0.15}>
              <p className="mt-6 text-center font-display text-lg uppercase tracking-wide text-[#7ED321] sm:text-xl">
                Together, we are building Zentra one product, one grower and one customer at a time.
              </p>
            </Reveal>
          </div>
        </section>

        {/* WHEN TO ENJOY */}
        <section id="when" data-testid="meet-when" className="border-t border-white/10 px-5 py-16 lg:px-12">
          <div className="mx-auto max-w-[1400px]">
            <SectionHead overline="Everyday Moments" title="When can you enjoy Zentra?" />
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              {[
                { icon: Dumbbell, t: "After your workout", d: "A convenient protein snack." },
                { icon: Briefcase, t: "During a busy workday", d: "A smarter option for everyday snacking." },
                { icon: Truck, t: "On the go", d: "Easy to carry and convenient to enjoy." },
                { icon: Coffee, t: "Between meals", d: "A convenient addition to your daily routine." },
              ].map((c, i) => (
                <Reveal key={c.t} delay={i * 0.06}>
                  <div data-testid={`meet-when-${c.t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="h-full rounded-2xl border border-white/10 bg-[#121212] p-5 transition-colors duration-300 hover:border-[#7ED321]/40">
                    <c.icon className="h-5 w-5 text-[#7ED321]" />
                    <h3 className="mt-4 font-display text-lg uppercase leading-tight tracking-wide text-white">{c.t}</h3>
                    <p className="mt-2 text-xs leading-relaxed text-[#A6A66A]">{c.d}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* CUSTOMER OFFER */}
        <section id="offer" data-testid="meet-offer" className="border-t border-white/10 px-5 py-16 lg:px-12">
          <div className="mx-auto max-w-[1000px] text-center">
            <SectionHead
              overline="Enjoy Zentra again"
              title="Loved your first bar?"
              text="Discover Zentra in a convenient 8-bar pack and save on your next purchase."
            />
            {OFFER.active && (
              <Reveal>
                <div className="mx-auto mb-8 max-w-md rounded-2xl border border-[#7ED321]/40 bg-[#7ED321]/10 p-5" data-testid="meet-offer-banner">
                  <p className="font-display text-2xl uppercase tracking-wide text-[#7ED321]">{OFFER.badge}</p>
                  <p className="mt-1 text-sm text-white/80">{OFFER.text}</p>
                </div>
              </Reveal>
            )}
            <div className="grid grid-cols-1 gap-4 text-left sm:grid-cols-3">
              {products.map((p, i) => (
                <Reveal key={p.sku} delay={i * 0.08} className="h-full">
                  <ProductCard product={p} />
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* INSTAGRAM */}
        <section id="social" data-testid="meet-social" className="border-t border-white/10 px-5 py-16 lg:px-12">
          <div className="mx-auto max-w-[800px] text-center">
            <Reveal>
              <Instagram className="mx-auto h-8 w-8 text-[#7ED321]" />
              <h2 className="mt-4 font-display text-3xl uppercase tracking-wide text-white sm:text-4xl">
                Follow the Zentra journey
              </h2>
              <p className="mt-3 text-sm tracking-[0.1em] text-[#A6A66A] sm:text-base">
                Microgreens. Nutrition. Fitness. Food. Farming.
              </p>
              <div className="mt-8 flex items-center justify-center gap-5" data-testid="meet-social-icons">
                <a
                  href="https://www.facebook.com/zentra.foods"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="meet-facebook-button"
                  aria-label="Zentra Foods on Facebook"
                  className="flex h-16 w-16 items-center justify-center rounded-full border-2 border-[#7ED321]/50 bg-[#7ED321]/10 text-[#7ED321] transition-all duration-300 hover:scale-110 hover:border-[#7ED321] hover:bg-[#7ED321] hover:text-[#0A0A0A]"
                >
                  <Facebook className="h-7 w-7" />
                </a>
                <a
                  href="https://www.instagram.com/zentra.foods?utm_source=qr&stkn=MXdtbjRkM3BueTFocA=="
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="meet-instagram-icon-button"
                  aria-label="Zentra Foods on Instagram"
                  className="flex h-16 w-16 items-center justify-center rounded-full border-2 border-[#7ED321]/50 bg-[#7ED321]/10 text-[#7ED321] transition-all duration-300 hover:scale-110 hover:border-[#7ED321] hover:bg-[#7ED321] hover:text-[#0A0A0A]"
                >
                  <Instagram className="h-7 w-7" />
                </a>
              </div>
            </Reveal>
          </div>
        </section>
      </main>

      {/* Legal strip */}
      <footer data-testid="meet-legal-strip" className="border-t border-white/10 px-5 pb-24 pt-6 md:pb-6">
        <div className="mx-auto flex max-w-[1000px] flex-col items-center gap-3 text-center">
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[11px] text-white/40" data-testid="footer-legal-links">
            <a href="/shipping-policy" data-testid="footer-link-shipping-policy" className="transition-colors duration-300 hover:text-[#7ED321]">Shipping Policy</a>
            <a href="/refund-policy" data-testid="footer-link-refund-policy" className="transition-colors duration-300 hover:text-[#7ED321]">Refund Policy</a>
            <a href="/privacy-policy" data-testid="footer-link-privacy-policy" className="transition-colors duration-300 hover:text-[#7ED321]">Privacy Policy</a>
            <a href="/terms" data-testid="footer-link-terms" className="transition-colors duration-300 hover:text-[#7ED321]">Terms &amp; Conditions</a>
          </div>
          <p className="text-[11px] text-white/30">© 2026 Mountain Fresh Farms LLP · Coimbatore, Tamil Nadu</p>
        </div>
      </footer>

      {/* Sticky mobile CTA bar */}
      <div className="fixed bottom-0 left-0 right-0 z-[80] border-t border-white/10 md:hidden"
        data-testid="meet-sticky-cta">
        <a href={waLink()} target="_blank" rel="noopener noreferrer" data-testid="meet-sticky-whatsapp"
          className="flex items-center justify-center gap-2 bg-[#25D366] py-4 text-xs font-bold uppercase tracking-[0.12em] text-white">
          <MessageCircle className="h-4 w-4" />
          Order on WhatsApp
        </a>
      </div>
    </div>
  );
};

export default MeetZentra;
